import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { AppLink } from "@/lib/apps";
import { appKeys } from "@/lib/query";
import { ApiError, getJson, sendJson } from "@/lib/time/api-client";
import type { AppMenuInput, MyAppsView } from "@/schemas/apps";

/**
 * The other Nymbl apps the signed-in person can open (GET /api/me/apps), and which of them show
 * in the left menu. Vinyl memberships change rarely; the server caches 10 minutes too. One try
 * only: on any failure the nav just leaves the section out.
 */
export function useMyApps() {
  return useQuery({
    queryKey: appKeys.mine,
    queryFn: () => getJson<MyAppsView>("/api/me/apps"),
    staleTime: 10 * 60_000,
    retry: false,
  });
}

/** Add one app to the left menu or take it out; the rest of the choice stays as it was. */
export function useToggleMenuApp() {
  const qc = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: ({ apps }: { app: AppLink; show: boolean; apps: AppMenuInput["apps"] }) =>
      sendJson<AppMenuInput>("PUT", "/api/me/apps/menu", { apps }),
    // Returning the refetch keeps the mutation pending (switches disabled) until the fresh menu
    // lands, so a quick second flip is never built from the old list.
    onSuccess: (_result, { app, show }) => {
      toast.success(show ? `${app.label} added to the menu` : `${app.label} removed from the menu`);
      return qc.invalidateQueries({ queryKey: appKeys.all });
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : "Something went wrong. Try again.");
    },
  });
}
